import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, FileText, CheckCircle2, AlertCircle, Download, FolderKanban, CheckSquare } from "lucide-react";
import { toast } from "sonner";

export default function ImportData() {
  const [uploading, setUploading] = useState(false);
  const [importType, setImportType] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const queryClient = useQueryClient();

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    setImportType(type);
    setUploading(true);
    setExtractedData(null);
    setImportResults(null);

    try {
      // Upload file
      const { file_url } = await base44.integrations.Core.UploadFile({ file });
      setUploadedFile(file_url);

      // Define schemas based on import type
      const schemas = {
        projects: {
          type: "object",
          properties: {
            name: { type: "string" },
            description: { type: "string" },
            status: { type: "string" },
            start_date: { type: "string" },
            end_date: { type: "string" },
            owner: { type: "string" },
            owner_name: { type: "string" },
          },
          required: ["name", "owner"]
        },
        tasks: {
          type: "object",
          properties: {
            title: { type: "string" },
            description: { type: "string" },
            status: { type: "string" },
            priority: { type: "string" },
            assigned_to: { type: "string" },
            assigned_to_name: { type: "string" },
            due_date: { type: "string" },
            project_name: { type: "string" },
          },
          required: ["title", "assigned_to"]
        }
      };

      // Extract data from file
      const result = await base44.integrations.Core.ExtractDataFromUploadedFile({
        file_url,
        json_schema: schemas[type]
      });

      if (result.status === "success") {
        setExtractedData(result.output);
        toast.success(`Fant ${Array.isArray(result.output) ? result.output.length : 1} ${type === 'projects' ? 'prosjekter' : 'oppgaver'}`);
      } else {
        toast.error(`Feil ved lesing av fil: ${result.details}`);
      }
    } catch (error) {
      toast.error("Kunne ikke laste opp fil");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleImport = async () => {
    if (!extractedData || !importType) return;

    setUploading(true);
    try {
      const dataArray = Array.isArray(extractedData) ? extractedData : [extractedData];
      let successCount = 0;
      let errorCount = 0;

      if (importType === "projects") {
        // Map owner email to user if owner_name is provided
        const mappedData = dataArray.map(project => {
          if (!project.owner && project.owner_name) {
            const user = users.find(u => u.full_name === project.owner_name);
            if (user) {
              project.owner = user.email;
            }
          }
          return project;
        });

        for (const project of mappedData) {
          try {
            await base44.entities.Project.create(project);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error("Error importing project:", error);
          }
        }
        queryClient.invalidateQueries({ queryKey: ["projects"] });
      } else if (importType === "tasks") {
        // Map assigned_to email to user if assigned_to_name is provided
        const mappedData = dataArray.map(task => {
          if (!task.assigned_to && task.assigned_to_name) {
            const user = users.find(u => u.full_name === task.assigned_to_name);
            if (user) {
              task.assigned_to = user.email;
              task.assigned_to_name = user.full_name;
            }
          }
          // Set defaults
          task.status = task.status || "todo";
          task.priority = task.priority || "medium";
          task.related_to_type = "none";
          return task;
        });

        for (const task of mappedData) {
          try {
            await base44.entities.Task.create(task);
            successCount++;
          } catch (error) {
            errorCount++;
            console.error("Error importing task:", error);
          }
        }
        queryClient.invalidateQueries({ queryKey: ["tasks"] });
      }

      setImportResults({ successCount, errorCount });
      toast.success(`Importert ${successCount} ${importType === 'projects' ? 'prosjekter' : 'oppgaver'}`);
      if (errorCount > 0) {
        toast.error(`${errorCount} feilet`);
      }
    } catch (error) {
      toast.error("Feil ved import");
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = (type) => {
    const templates = {
      projects: [
        ["name", "description", "status", "start_date", "end_date", "owner", "owner_name"],
        ["Nytt Prosjekt", "Prosjektbeskrivelse", "planning", "2025-01-01", "2025-12-31", "user@example.com", "Bruker Navn"]
      ],
      tasks: [
        ["title", "description", "status", "priority", "assigned_to", "assigned_to_name", "due_date", "project_name"],
        ["Ny Oppgave", "Oppgavebeskrivelse", "todo", "medium", "user@example.com", "Bruker Navn", "2025-12-31", "Prosjektnavn"]
      ]
    };

    const template = templates[type];
    const csvContent = template.map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${type}_template.csv`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-2">Importer Data</h1>
          <p className="text-slate-500">Last opp CSV-filer for å importere prosjekter og oppgaver</p>
        </div>

        {/* Instructions */}
        <Card className="p-6 bg-gradient-to-br from-blue-50 to-lime-100 border-blue-200 mb-6">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-slate-900 mb-2">Hvordan importere?</h3>
              <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                <li>Last ned en mal for prosjekter eller oppgaver</li>
                <li>Fyll ut CSV-filen med dine data</li>
                <li>Last opp filen og forhåndsvis dataene</li>
                <li>Bekreft import for å legge til i systemet</li>
              </ol>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Projects Import */}
          <Card className="bg-white border-0 shadow-sm">
            <div className="h-2 bg-gradient-to-r from-zinc-900 to-zinc-900" />
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-lime-100 to-lime-200 flex items-center justify-center flex-shrink-0">
                  <FolderKanban className="w-6 h-6 text-zinc-900" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Prosjekter</h3>
                  <p className="text-sm text-slate-600">Importer flere prosjekter samtidig</p>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => downloadTemplate("projects")}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Last ned CSV-mal
                </Button>

                <label className="block">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileUpload(e, "projects")}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Button 
                    className="w-full bg-zinc-900 hover:bg-zinc-900"
                    disabled={uploading}
                    onClick={(e) => e.currentTarget.previousElementSibling.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading && importType === "projects" ? "Laster opp..." : "Last opp CSV"}
                  </Button>
                </label>
              </div>

              {importType === "projects" && extractedData && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700">
                    ✓ {Array.isArray(extractedData) ? extractedData.length : 1} prosjekter klar for import
                  </p>
                </div>
              )}
            </div>
          </Card>

          {/* Tasks Import */}
          <Card className="bg-white border-0 shadow-sm">
            <div className="h-2 bg-gradient-to-r from-blue-500 to-blue-600" />
            <div className="p-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center flex-shrink-0">
                  <CheckSquare className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">Oppgaver</h3>
                  <p className="text-sm text-slate-600">Importer flere oppgaver samtidig</p>
                </div>
              </div>

              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => downloadTemplate("tasks")}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Last ned CSV-mal
                </Button>

                <label className="block">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => handleFileUpload(e, "tasks")}
                    className="hidden"
                    disabled={uploading}
                  />
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={uploading}
                    onClick={(e) => e.currentTarget.previousElementSibling.click()}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading && importType === "tasks" ? "Laster opp..." : "Last opp CSV"}
                  </Button>
                </label>
              </div>

              {importType === "tasks" && extractedData && (
                <div className="mt-4 p-3 bg-emerald-50 rounded-lg">
                  <p className="text-sm text-emerald-700">
                    ✓ {Array.isArray(extractedData) ? extractedData.length : 1} oppgaver klar for import
                  </p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Preview and Import */}
        {extractedData && (
          <Card className="bg-white border-0 shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Forhåndsvisning</h3>
              <Badge className="bg-blue-100 text-blue-700">
                {Array.isArray(extractedData) ? extractedData.length : 1} elementer
              </Badge>
            </div>

            <div className="max-h-96 overflow-auto mb-4">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    {Object.keys(Array.isArray(extractedData) ? extractedData[0] : extractedData).map(key => (
                      <th key={key} className="px-4 py-2 text-left text-slate-700 font-medium">
                        {key}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(Array.isArray(extractedData) ? extractedData : [extractedData]).map((item, idx) => (
                    <tr key={idx} className="border-t border-slate-100">
                      {Object.values(item).map((value, vIdx) => (
                        <td key={vIdx} className="px-4 py-2 text-slate-600">
                          {String(value)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3">
              <Button 
                onClick={handleImport}
                disabled={uploading}
                className="bg-slate-900 hover:bg-slate-800"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {uploading ? "Importerer..." : "Bekreft Import"}
              </Button>
              <Button 
                variant="outline"
                onClick={() => {
                  setExtractedData(null);
                  setImportType(null);
                  setUploadedFile(null);
                }}
              >
                Avbryt
              </Button>
            </div>
          </Card>
        )}

        {/* Results */}
        {importResults && (
          <Card className="bg-white border-0 shadow-sm p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 mb-2">Import fullført!</h3>
                <div className="space-y-1 text-sm text-slate-600">
                  <p>✓ {importResults.successCount} elementer importert</p>
                  {importResults.errorCount > 0 && (
                    <p className="text-rose-600">✗ {importResults.errorCount} feilet</p>
                  )}
                </div>
                <Button 
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    setImportResults(null);
                    setExtractedData(null);
                    setImportType(null);
                    setUploadedFile(null);
                  }}
                >
                  Importer mer
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}